import React from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MuiDrawer } from '@ace/ace-ui-web';
import { GridRenderContext, getKpiValueText, type KpiValueRow, type KpiAmount } from './cellRenderers';
import { useBuildDynamicUrl } from '../../hooks/useDynamicUrl';
import { useAppSelector } from '../../redux/hooks';
import { selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
import type { ClickHandler } from '../../utils/usageMetricsLogger';

/**
 * The four columns whose values come from the backend and can be long enough to get cut off:
 * Goal, Prior, Current, and 80% Benchmark. When auto-fit is on, each of these is widened just
 * enough to show its longest value in full. If the screen is still too narrow to fit them, the
 * old behavior takes over: the value is cut off with a tooltip that shows it in full on hover.
 */
const AUTOFIT_VALUE_FIELDS = ['kpiGoal', 'priorKpi', 'currentKpi', 'benchmark'] as const;
/** For each column, which field in the row data holds the value it shows (so we know what to measure). */
const AUTOFIT_AMOUNT_KEYS: Record<string, string> = {
  kpiGoal: 'kpiGoal',
  priorKpi: 'priorKpiAmount',
  currentKpi: 'currentKpiAmount',
  benchmark: 'benchmark',
};
/** The font the cells are drawn in. We must measure text in this same font to get the right width. */
const AUTOFIT_FONT = '16px Roboto, Helvetica, Arial, sans-serif';
/** Extra width added on top of the measured text, so the value isn't jammed against the cell edge. */
const AUTOFIT_PADDING = 32;

/**
 * Measures how wide a piece of text will be on screen, in pixels. It measures off-screen, so it
 * works for every row right away — even before the table has been drawn.
 */
let autoFitCanvas: HTMLCanvasElement | null = null;
const measureTextWidth = (text: string): number => {
  if (typeof document === 'undefined') return 0;
  if (!autoFitCanvas) autoFitCanvas = document.createElement('canvas');
  const context = autoFitCanvas.getContext('2d');
  if (!context) return 0;
  context.font = AUTOFIT_FONT;
  return context.measureText(text).width;
};

/**
 * Props interface for the DataGrid component
 * @template T - The type of data rows in the grid
 * @template TArgs - Tuple type for additional column factory arguments
 */
interface DataGridProps<T, TArgs extends unknown[] = []> {
  /** Array of data to display in the grid */
  rows: T[];
  /** Function to get unique row ID */
  getRowId: (row: T) => string | number;
  /** Factory function to create column definitions */
  createColumns: (context: GridRenderContext, ...args: TArgs) => GridColDef[];
  /** Additional arguments to pass to createColumns function */
  columnArgs?: TArgs;
  /** Optional key to force re-render when columns change */
  gridKey?: string;
  /** Whether this grid is being rendered in print view mode */
  isPrintView?: boolean;
  /** Optional click handler for usage metrics logging, passed through to GridRenderContext */
  logClick?: ClickHandler;
  /** Optional handler to open the disclaimer dialog, passed through to GridRenderContext */
  onDisclaimerOpen?: () => void;
  /**
   * When true, value columns (Goal/Prior/Current/Benchmark) auto-grow to fit their
   * widest value by measuring rendered content, pulling width from utility columns,
   * while the KPI column stays fixed. Truncation + tooltip remains the fallback.
   * Opt-in (used only by the B2B grid); off by default for all other grids.
   */
  autoFitValueColumns?: boolean;
}

/**
 * Generic DataGrid component with drawer functionality
 * Accepts dynamic column configurations through factory function
 * @template T - The type of data rows in the grid
 * @template TArgs - Tuple type for additional column factory arguments
 */
const MuiDataGrid = <T, TArgs extends unknown[] = []>({
  rows,
  getRowId,
  createColumns,
  columnArgs = [] as TArgs,
  gridKey,
  isPrintView = false,
  logClick,
  onDisclaimerOpen,
  autoFitValueColumns = false,
}: DataGridProps<T, TArgs>) => {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState<boolean>(false);
  const [drawerContent, setDrawerContent] = React.useState<{
    title: string;
    content: React.ReactNode;
    type: 'action' | 'info';
    width?: number;
  }>({
    title: '',
    content: null,
    type: 'action',
  });

  // Track viewport width for responsive column sizing
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  );

  // Update screen size on resize
  React.useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth <= 768;
      setIsSmallScreen(smallScreen);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Executes the measurement (only runs when autoFitValueColumns is on, i.e. the B2B grid) ---
  // For each value column, look at every row, find its longest value, and work out how wide the
  // column must be to show that value in full. Reading straight from the data means even a long
  // "$..." value in any row is taken into account.
  const autoFitMinWidths = React.useMemo<Record<string, number>>(() => {
    if (!autoFitValueColumns) return {};
    const widths: Record<string, number> = {};
    AUTOFIT_VALUE_FIELDS.forEach(field => {
      const amountKey = AUTOFIT_AMOUNT_KEYS[field];
      let widest = 0;
      (rows as ReadonlyArray<KpiValueRow & Record<string, KpiAmount>>).forEach(row => {
        const text = getKpiValueText(row, field, row[amountKey]);
        if (text) widest = Math.max(widest, measureTextWidth(text));
      });
      if (widest > 0) widths[field] = Math.ceil(widest) + AUTOFIT_PADDING;
    });
    return widths;
  }, [autoFitValueColumns, rows]);

  // Create columns using factory pattern with drawer context
  const buildDynamicUrl = useBuildDynamicUrl();
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  const columns = React.useMemo(
    () => {
      const cols = createColumns(
        {
          setDrawerContent,
          setIsDrawerOpen,
          buildDynamicUrl,
          isMultiStoreEnabled,
          handleAndLogClick: logClick,
          onDisclaimerOpen,
          isPrintView,
        },
        ...columnArgs,
      );

      // Filter out interactive columns in print view
      let filteredCols = cols;
      if (isPrintView) {
        filteredCols = cols.filter(
          col =>
            col.field !== 'moreInfo' &&
            col.field !== 'takeActionTitle' &&
            col.field !== 'takeAction',
        );
      }

      // Only adjust the value/donor columns when at least one value is actually wider than its
      // column's normal minimum. If everything already fits (e.g. single-store or small values),
      // we leave every column exactly as it was — no widening, no space-sharing.
      const valueNeedsWidening =
        autoFitValueColumns &&
        filteredCols.some(
          col =>
            (AUTOFIT_VALUE_FIELDS as readonly string[]).includes(col.field) &&
            (autoFitMinWidths[col.field] ?? 0) > (typeof col.minWidth === 'number' ? col.minWidth : 0),
        );

      // Dynamically adjust KPI column width based on screen size
      return filteredCols.map(col => {
        if (col.field === 'kpi') {
          // KPI is the first column: keep it a fixed width so its wording is never squeezed onto two lines.
          return {
            ...col,
            minWidth: isSmallScreen ? 140 : 260,
            width: isSmallScreen ? 140 : 260,
          };
        }
        // Nothing to do unless auto-fit is on AND a value actually needs more room.
        if (!valueNeedsWidening) {
          return col;
        }
        // Value columns: make each at least as wide as its longest value (so nothing is cut off),
        // and let them take a bigger share of any spare space than the lighter columns below.
        if ((AUTOFIT_VALUE_FIELDS as readonly string[]).includes(col.field)) {
          const measured = autoFitMinWidths[col.field];
          const baseMinWidth = typeof col.minWidth === 'number' ? col.minWidth : 0;
          const minWidth = measured ? Math.max(baseMinWidth, measured) : baseMinWidth;
          return { ...col, flex: 1, minWidth };
        }
        // These columns give up some of their space so the value columns above can grow.
        // More Info keeps a minimum width so its "View info" link never wraps onto two lines.
        if (col.field === 'moreInfo') {
          return { ...col, flex: 0.5, minWidth: 100 };
        }
        if (col.field === 'takeActionTitle') {
          return { ...col, flex: 1.2 };
        }
        if (col.field === 'kpiProgress') {
          return { ...col, flex: 0.6, minWidth: 50, maxWidth: 90 };
        }
        return col;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      createColumns,
      isSmallScreen,
      buildDynamicUrl,
      isMultiStoreEnabled,
      logClick,
      isPrintView,
      onDisclaimerOpen,
      autoFitValueColumns,
      autoFitMinWidths,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ...columnArgs,
    ],
  );

  return (
    <>
      <DataGrid
        key={gridKey}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        autoHeight
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        disableColumnResize={true}
        disableColumnSorting={true}
        getRowHeight={() => 'auto'}
        getRowClassName={params =>
          params.indexRelativeToCurrentPage % 2 === 1 ? 'striped-row' : ''
        }
        sx={{
          border: 'none',
          borderLeft: '1px solid #c9c9c9',
          borderBottom: '1px solid #c9c9c9',
          borderRadius: 0,
          width: '100%',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'var(--color-grey-200)',
            fontWeight: 600,
            fontSize: '14px',
            borderBottom: 'none',
            minHeight: '65px',
            maxHeight: '65px',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#eeeeee',
            borderRight: '1px solid #c9c9c9',
            height: '65px',
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #c9c9c9',
            borderTop: 'none',
            minHeight: '62px',
            '&:last-child': {
              borderBottom: 'none',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            borderTop: 'none',
            borderRight: '1px solid #c9c9c9',
            fontSize: '16px',
            whiteSpace: 'normal',
            lineHeight: 'normal',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            minHeight: '62px',
            '&:last-child': {
              borderRight: 'none',
            },
          },
          '& .MuiDataGrid-cellContent': {
            borderBottom: 'none',
            borderTop: 'none',
          },
          '& .striped-row': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f5f5f5',
          },
          // Sticky first column (kpi field)
          '& .MuiDataGrid-columnHeader[data-field="kpi"]': {
            position: 'sticky',
            left: 0,
            zIndex: 3,
            borderRight: '2px solid #757575',
            fontWeight: 500,
            backgroundColor: '#eeeeee',
          },
          '& .MuiDataGrid-cell[data-field="kpi"]': {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            borderRight: '2px solid #757575',
            fontWeight: 500,
            fontSize: 14,
            backgroundColor: '#ffffff',
          },
          '& .striped-row .MuiDataGrid-cell[data-field="kpi"]': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="kpi"]': {
            backgroundColor: '#f5f5f5',
          },
        }}
      />

      <MuiDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        anchor="right"
        headerText={drawerContent.title}
        {...(drawerContent.width !== undefined && {
          autoWidth: false,
          width: drawerContent.width,
        })}>
        {drawerContent.content}
      </MuiDrawer>
    </>
  );
};

export default MuiDataGrid;
